/* eslint-disable */
import { getLicenseById, getLicenseList } from "@/api/license";
import { isOnlineSilent } from "@/api/omniwa";
import { getXAuthAgent } from "@/utils/auth";

// Membersihkan nomor WhatsApp/JID menjadi digit saja. Dipakai untuk
// membandingkan nomor lisensi dan nomor pelanggan tanpa bergantung pada
// suffix @s.whatsapp.net atau @lid.
export function normalizePhoneNumber(value) {
  const raw = String(value || "").trim();
  if (!raw) {
    return "";
  }
  const localPart = raw.split("@")[0];
  const digits = localPart.replace(/\D/g, "");
  return digits || localPart.toLowerCase();
}

export function isValidPhoneNumber(value) {
  const normalized = normalizePhoneNumber(value);
  return normalized.length >= 6 && normalized.length <= 20;
}

function licenseMatchesPhone(license, normalizedPhone) {
  return normalizePhoneNumber(license && license.license_phone_number) === normalizedPhone;
}

function licenseError(message) {
  const error = new Error(message);
  error.isChatRoomError = true;
  return error;
}

// Menentukan lisensi yang menangani sebuah sesi AI.
// - license_id dari sesi dipakai lebih dulu bila backend menyediakannya.
// - Bila tidak ada, nomor lisensi pada sesi dicocokkan ke daftar lisensi member.
export async function resolveSessionLicense(store, session) {
  const customerPhone = normalizePhoneNumber(session && session.message_number);
  if (!isValidPhoneNumber(customerPhone)) {
    throw licenseError("Nomor pelanggan pada sesi ini tidak valid.");
  }

  const sessionLicenseId = session && (session.license_id || session.licenseId);
  const sessionLicensePhone = normalizePhoneNumber(session && session.license_phone_number);
  let license = null;

  if (sessionLicenseId) {
    try {
      const response = await getLicenseById(sessionLicenseId, { silent: true });
      license = response && response.data ? response.data : null;
    } catch (error) {
      license = null;
    }
  }

  if (!license) {
    if (!isValidPhoneNumber(sessionLicensePhone)) {
      throw licenseError(
        "Lisensi WhatsApp untuk sesi ini tidak dapat diidentifikasi. Buka Chat Room dari halaman Lisensi WhatsApp."
      );
    }

    let response = null;
    try {
      response = await getLicenseList({
        license_name: "",
        license_phone_number: sessionLicensePhone,
        license_status: "",
        page: 0,
        perPage: 20
      }, { silent: true });
    } catch (error) {
      response = null;
    }

    const payload = response && response.data ? response.data : {};
    const rows = Array.isArray(payload.data) ? payload.data : [];
    const matches = rows.filter(row => licenseMatchesPhone(row, sessionLicensePhone));

    if (matches.length > 1) {
      throw licenseError(
        "Lebih dari satu lisensi memiliki nomor yang sama. Buka Chat Room dari halaman Lisensi WhatsApp."
      );
    } else if (matches.length === 1) {
      license = matches[0];
    }
  }

  if (!license || !license.id) {
    throw licenseError("Lisensi WhatsApp untuk sesi ini tidak ditemukan.");
  }

  if (Number(license.is_chat) !== 1) {
    throw licenseError(
      "Chat Room belum diaktifkan untuk lisensi " +
        (license.license_phone_number || license.license_name || license.id) +
        ". Aktifkan dulu di halaman Lisensi WhatsApp."
    );
  }

  return license;
}

async function verifyLicenseConnection(license) {
  const phone = normalizePhoneNumber(license && license.license_phone_number);
  if (!isValidPhoneNumber(phone)) {
    throw licenseError("Nomor lisensi WhatsApp tidak valid.");
  }

  let response = null;
  try {
    response = await isOnlineSilent(phone);
  } catch (error) {
    throw licenseError(
      "Status koneksi lisensi " + phone + " belum dapat diverifikasi. Coba lagi."
    );
  }

  const data = response && response.data ? response.data : {};
  if (Number(data.status) !== 1) {
    throw licenseError(
      "Lisensi " +
        phone +
        " sedang tidak terhubung. Periksa koneksi WhatsApp atau pindai QR terlebih dahulu."
    );
  }

  return true;
}

async function activateLicenseForChatRoom(store, license) {
  const session = (store.state.agent && store.state.agent.agentSession) || {};
  const sameLicense =
    String(session.license_id || "") === String(license.id || "");
  const isOwner = Number(session.is_owner) === 1;
  const hasAgentToken = !!getXAuthAgent();

  if (sameLicense && isOwner && hasAgentToken) {
    return session;
  }

  const response = await store.dispatch("agent/connectToAgentOwner", {
    license_id: license.id,
    agent_id: "",
    silent: true
  });

  const data = response && response.data ? response.data : {};
  if (
    !data ||
    !data.token ||
    String(data.license_id || "") !== String(license.id)
  ) {
    throw licenseError("Lisensi belum berhasil diaktifkan. Silakan coba lagi.");
  }

  return data;
}

export function navigateToChatRoom(licenseId, phone, roomId) {
  const baseUrl = String(process.env.VUE_APP_BASE_URL || "").replace(/\/+$/, "");
  const normalizedPhone = normalizePhoneNumber(phone);
  const params = [
    "license_id=" + encodeURIComponent(licenseId),
    "phone=" + encodeURIComponent(normalizedPhone)
  ];
  if (roomId) {
    params.push("room_id=" + encodeURIComponent(roomId));
  }
  window.location.href = baseUrl + "/omni?" + params.join("&");
}

// Alur utama Lihat Chat:
// 1. identifikasi lisensi dari sesi;
// 2. pastikan Chat Room aktif dan lisensi terhubung;
// 3. aktifkan lisensi dengan mekanisme Buka Chat Room di /license/list;
// 4. arahkan ke /omni dengan nomor pelanggan (dan room_id bila tersedia).
export async function openChatRoomForSession(store, session) {
  const customerPhone = normalizePhoneNumber(session && session.message_number);
  if (!isValidPhoneNumber(customerPhone)) {
    throw licenseError("Nomor pelanggan pada sesi ini tidak valid.");
  }

  const license = await resolveSessionLicense(store, session);
  await verifyLicenseConnection(license);
  try {
    await activateLicenseForChatRoom(store, license);
  } catch (error) {
    throw licenseError(
      "Lisensi " +
        (license.license_phone_number || license.id) +
        " belum berhasil diaktifkan. Silakan coba lagi."
    );
  }

  navigateToChatRoom(license.id, customerPhone, session && session.room_id);
  return {
    license: license,
    phone: customerPhone,
    roomId: (session && session.room_id) || ""
  };
}



// WEBPACK FOOTER //
// ./src/utils/chatRoom.js